import {
  Job,
  JobSalaryMatchQuery,
  UpdateMinSalaryInput,
  UpdateMinSalaryMutation,
  useJobSalaryMatchLazyQuery,
  useUpdateMinSalaryMutation,
} from 'generated/apolloComponents'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import Tippy from '@tippyjs/react'
import MinSalaryInput from './MinSalaryInput'
import { useMe } from '@/context/meContext'

import 'tippy.js/dist/tippy.css'

interface IProps {
  job: Job
  [x: string]: any
  minSalaryFilter?: number | null
}
type Variables = {
  input: UpdateMinSalaryInput
}

interface IXProps {
  matchingUserBrackets: boolean
  minSalarySent: boolean
}

export const SalaryKnown = ({
  matchingUserBrackets,
  minSalarySent,
}: IXProps) => {
  const { t } = useTranslation('common')
  return (
    <>
      {minSalarySent
        ? matchingUserBrackets
          ? t('common:brackets_matching')
          : t('common:brackets_not_matching')
        : t('common:brackets_known')}
    </>
  )
}
const NoSalary = ({ job, minSalaryFilter, ...rest }: IProps) => {
  const { t } = useTranslation('common')
  const { me } = useMe()
  const { data: session } = useSession()

  const [isSalaryFilter, setIsSalaryFilter] = useState<boolean>(
    minSalaryFilter ? minSalaryFilter !== 0 : false
  )
  const [matchingBrackets, setMatchingBrackets] = useState(
    job.matchingUserBrackets
  )
  const [minSalarySent, setMinSalarySent] = useState(
    me ? me.minSalary !== null : false
  )

  useEffect(() => {
    let mounted = true
    const setStateAction = async () => {
      setIsSalaryFilter(minSalaryFilter ? minSalaryFilter !== 0 : false)
    }
    return () => {
      mounted = false
    }
  }, [minSalaryFilter])

  const [updateMinSalary] = useUpdateMinSalaryMutation({
    onCompleted: (data: UpdateMinSalaryMutation) => {
      setMinSalarySent(true)
    },
  })

  const [matchingUserBrackets] = useJobSalaryMatchLazyQuery({
    onCompleted: (data: JobSalaryMatchQuery) => {
      setMatchingBrackets(data.jobSalaryMatch.matchingUserBrackets)
    },
  })

  const handleMinSalaryChange = async (value: any) => {
    if (session)
      updateMinSalary({
        variables: {
          input: {
            minSalary: value,
          },
        },
      })

    matchingUserBrackets({
      variables: {
        jobId: job.id,
        minSalary: value,
      },
    })
    setMinSalarySent(true)
  }

  const showMinSalaryInput = (
    minSalarySent: boolean,
    showSalary: boolean,
    hiddenBrackets: boolean
  ) => {
    if (!minSalarySent) {
      if (!showSalary && !hiddenBrackets) {
        return null
      }
      return (
        <MinSalaryInput
          job={job}
          handleMinSalaryChange={handleMinSalaryChange}
        />
      )
    }
    return null
  }

  return (
    <div {...rest} className='relative'>
      <p
        className={`${
          job.hiddenBrackets
            ? 'text-lg'
            : !job.showSalary && !job.hiddenBrackets && 'text-gray-300'
        }`}
      >
        {job.hiddenBrackets ? (
          <SalaryKnown
            matchingUserBrackets={matchingBrackets}
            minSalarySent={minSalarySent}
          />
        ) : (
          !job.showSalary && !job.hiddenBrackets && t('common:brackets_empty')
        )}
        {job.hiddenBrackets ? (
          <Tippy content={t('common:brackets_hidden_known')}>
            <span
              className='cursor-pointer '
              data-padding='16px 16px'
              data-class='ml-10 w-72 shadow-dropdown font-normal text-sm '
            >
              <InformationCircleIcon
                width={24}
                className='ml-2 mb-1 hover:text-gray-300'
                style={{ maxWidth: '14px', maxHeight: '14px' }}
              />
            </span>
          </Tippy>
        ) : null}
      </p>
      {job.hiddenBrackets &&
        showMinSalaryInput(
          minSalarySent,
          job.showSalary,
          job.hiddenBrackets as boolean
        )}
    </div>
  )
}

export default NoSalary
